import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EQueryKeys } from 'constants/enums';

import { GeneralThemeFormValues } from 'components/Theme/GeneralSettings/generalTheme.types';

import useAxios from '../hooks/useAxios';

import { EApiRoutes, fetchConfig, getApiRouteOrMock } from './api.utils';

export interface StoreSettingsValues {
  storeTabName?: string;
  logoutFont?: string;
  bouncyScrollOn?: boolean;
  favicon?: string;
  loaderAnimation?: string;
  storeSaleImage?: string;
  defaultPlayerImage?: string;
  footer?: {
    appstoreLink?: string;
    googlePlayLink?: string;
    privacyPolicyUrl?: string;
    termsAndConditionsUrl?: string;
    supportLink?: string;
    openLinksInNewTab?: boolean;
    showCookieSettings?: boolean;
    enableRefundFooterLink?: boolean;
    enabledRefundFooterLinkCountries?: string[];
    supportParams?: {
      playerIdParamName?: string;
      playerNameParamName?: string;
      playerEmailParamName?: string;
    };
  };
  emptyState?: {
    noOffersTitleText?: string;
    noOffersMessageText?: string;
  };
  seo?: {
    title?: string;
    description?: string;
    keywords?: string[];
    socialShareImageUrl?: string;
    socialTitle?: string;
    socialDescription?: string;
  };
  maintenanceModeRichText?: GeneralThemeFormValues['maintenanceModeRichText'];
}

export default function useStoreSettings(currentPublisherId?: string) {
  const axios = useAxios();
  const route = getApiRouteOrMock(EApiRoutes.STORE_SETTINGS);

  const getStoreSettings = useQuery<StoreSettingsValues, Error>({
    ...fetchConfig.general,
    retry: false,
    queryKey: [EQueryKeys.STORE_SETTINGS, currentPublisherId],
    queryFn: async () =>
      await axios.get(
        route,
        {},
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      ),
    enabled: !!currentPublisherId
  });

  const updateStoreSettings = useMutation(
    async (storeSettings: Partial<StoreSettingsValues>) => {
      return await axios.put(
        route,
        storeSettings,
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
    },
    {}
  );

  return {
    getStoreSettings,
    updateStoreSettings
  };
}

export function useStoreSettingsSectionMutation(currentPublisherId?: string) {
  const axios = useAxios();
  const queryClient = useQueryClient();
  const route = getApiRouteOrMock(EApiRoutes.STORE_SETTINGS);

  const mutation = useMutation(
    async (storeSettings: Partial<StoreSettingsValues>) => {
      return await axios.put(
        route,
        storeSettings,
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          EQueryKeys.STORE_SETTINGS,
          currentPublisherId
        ]);
      }
    }
  );

  return mutation;
}

export function useShowRotatePromptMutation(currentPublisherId?: string) {
  const axios = useAxios();
  const queryClient = useQueryClient();
  const route = `${getApiRouteOrMock(EApiRoutes.GENERAL_THEME)}/showRotatePrompt`;

  const mutation = useMutation(
    async (showRotatePrompt: boolean) => {
      return await axios.patch(
        route,
        { showRotatePrompt },
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          EQueryKeys.GENERAL_THEME + (currentPublisherId ?? '')
        ]);
      }
    }
  );

  return mutation;
}
